import { useState, useEffect, useCallback, useRef } from 'react';

export interface DetailedRoomMetrics {
  translationStats: {
    totalTranslations: number;
    successRate: number;
    failureRate: number;
    averageLatency: number;
    latencyByLanguage: Record<string, number>;
  };
  connections: {
    active: number;
    total: number;
    byLanguage: Record<string, number>;
  };
  audio: {
    bufferSize: number;
    chunksReceived: number;
    chunksProcessed: number;
    processingRate: number;
  };
  errors: {
    total: number;
    byType: Record<string, number>;
    recent: Array<{
      code: string;
      message: string;
      timestamp: string;
      severity?: string;
    }>;
  };
  system: {
    uptime: number;
    lastUpdate: string;
  };
  timeSeries: Array<{
    timestamp: string;
    translationCount: number;
    averageLatency: number;
    errorCount: number;
    activeConnections: number;
    audioChunksProcessed: number;
  }>;
}

export interface UseMetricsOptions {
  refreshInterval?: number; // milliseconds
  enabled?: boolean;
}

export function useMetrics(roomId: string, options: UseMetricsOptions = {}) {
  const { refreshInterval = 5000, enabled = true } = options;

  const [metrics, setMetrics] = useState<DetailedRoomMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const intervalRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const fetchMetrics = useCallback(async () => {
    if (!enabled || !roomId) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      // Use /admin/rooms/status API which exists
      const response = await fetch(`${apiUrl}/admin/rooms/status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch metrics: ${response.statusText}`);
      }

      const data = await response.json();

      // Find the specific room's data
      const roomData = data.rooms?.find((r: any) => r.roomId === roomId);

      if (!roomData) {
        throw new Error(`Room ${roomId} not found`);
      }

      // Transform API response to DetailedRoomMetrics format
      const transformedMetrics: DetailedRoomMetrics = {
        translationStats: {
          totalTranslations: roomData.translationMetrics?.totalTranslations || 0,
          successRate: 1 - (roomData.translationMetrics?.errorRate || 0),
          failureRate: roomData.translationMetrics?.errorRate || 0,
          averageLatency: Math.round((roomData.translationMetrics?.averageLatency || 0) * 1000),
          latencyByLanguage: {},
        },
        connections: {
          active: roomData.activeConnections || 0,
          total: roomData.activeConnections || 0,
          byLanguage: roomData.connectionsByLanguage || {},
        },
        audio: {
          bufferSize: roomData.audioConfig?.currentBufferSize || 0,
          chunksReceived: 0,
          chunksProcessed: 0,
          processingRate: 0,
        },
        errors: {
          total: 0,
          byType: {},
          recent: [],
        },
        system: {
          uptime: 0,
          lastUpdate: roomData.lastAudioReceived || new Date().toISOString(),
        },
        timeSeries: [],
      };

      if (mountedRef.current) {
        setMetrics(transformedMetrics);
        setError(null);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setIsLoading(false);
      }
    }
  }, [roomId, enabled]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchMetrics();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchMetrics]);

  // Setup polling
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval
    intervalRef.current = window.setInterval(fetchMetrics, refreshInterval);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchMetrics, refreshInterval, enabled]);

  const refresh = useCallback(() => {
    setIsLoading(true);
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    isLoading,
    error,
    refresh,
  };
}
